// RulesAcquisitionGame.jsx - RAAT Rules Acquisition Aptitude Test
// Open-book comprehension test - read passage and answer questions WITH reference to text
// Mobile-friendly interface

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  Monitor,
  Smartphone,
  CheckCircle,
  XCircle,
  RotateCcw,
  FileText,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  BookOpen,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  INSTRUCTIONS: 'instructions',
  PLAYING: 'playing',
  FEEDBACK: 'feedback',
  FINISHED: 'finished',
};

const LEVEL_LABELS = {
  1: 'Basic procedures',
  2: 'Safety regulations',
  3: 'Complex protocols',
  4: 'Multi-part procedures',
  5: 'Expert-level rules',
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// RAAT Passages with questions - can refer back to passage while answering
const RAAT_PASSAGES = {
  1: [
    {
      title: "Reporting Safety Critical Incidents",
      passage: `All staff must report safety critical incidents immediately to the Train Operations Controller (TOC). Safety critical incidents include any event that could potentially cause harm to staff, passengers, or the public.

When reporting an incident, staff must provide the following information in this order:
1. Their name and staff number
2. The exact location (station name or mileage marker)
3. A brief description of the incident
4. Whether any injuries have occurred
5. Whether emergency services are required

Reports must be made within 15 minutes of the incident occurring. If the staff member is directly involved in managing the incident, they should delegate the reporting to another colleague if possible.

Following the initial report, a written incident form (Form SR-47) must be completed within 24 hours and submitted to the Duty Manager.`,
      questions: [
        {
          question: "To whom must safety critical incidents be reported?",
          options: [
            { text: "Duty Manager", correct: false },
            { text: "Train Operations Controller (TOC)", correct: true },
            { text: "Station Supervisor", correct: false },
            { text: "Emergency Services", correct: false },
          ],
        },
        {
          question: "What is the time limit for making an initial incident report?",
          options: [
            { text: "5 minutes", correct: false },
            { text: "15 minutes", correct: true },
            { text: "30 minutes", correct: false },
            { text: "1 hour", correct: false },
          ],
        },
        {
          question: "What form must be completed following an incident?",
          options: [
            { text: "Form IR-12", correct: false },
            { text: "Form SC-24", correct: false },
            { text: "Form SR-47", correct: true },
            { text: "Form IM-15", correct: false },
          ],
        },
        {
          question: "What is the first piece of information to provide when reporting?",
          options: [
            { text: "Location of the incident", correct: false },
            { text: "Description of the incident", correct: false },
            { text: "Name and staff number", correct: true },
            { text: "Whether injuries occurred", correct: false },
          ],
        },
      ],
    },
    {
      title: "Platform Safety Procedures",
      passage: `All platform staff must wear high-visibility vests at all times when on the platform. The vest must be fastened and visible from both front and back.

The yellow safety line marks the safe standing area. Staff must remain behind this line unless:
- They are assisting a passenger who requires help
- They are performing an authorised safety check
- An announcement has been made that no trains are approaching

When a train approaches, staff must:
1. Move behind the yellow line immediately
2. Face the approaching train
3. Remain stationary until the train has stopped completely
4. Only approach the train when doors have opened

Staff must never attempt to stop a moving train by waving or signalling unless there is an immediate danger to life.`,
      questions: [
        {
          question: "When can staff cross the yellow safety line?",
          options: [
            { text: "When directing passengers", correct: false },
            { text: "When assisting a passenger who requires help", correct: true },
            { text: "When a train is departing", correct: false },
            { text: "When the platform is busy", correct: false },
          ],
        },
        {
          question: "What should staff do when a train approaches?",
          options: [
            { text: "Wave to acknowledge the driver", correct: false },
            { text: "Continue their duties normally", correct: false },
            { text: "Move behind the yellow line and face the train", correct: true },
            { text: "Move to the platform edge to assist boarding", correct: false },
          ],
        },
        {
          question: "When is signalling to stop a train permitted?",
          options: [
            { text: "When passengers are still boarding", correct: false },
            { text: "When there is immediate danger to life", correct: true },
            { text: "When the platform is overcrowded", correct: false },
            { text: "When requested by a passenger", correct: false },
          ],
        },
        {
          question: "What must be visible on the high-visibility vest?",
          options: [
            { text: "Staff name badge", correct: false },
            { text: "The vest must be visible from front and back", correct: true },
            { text: "Company logo only", correct: false },
            { text: "Reflective strips on sleeves", correct: false },
          ],
        },
      ],
    },
    {
      title: "Radio Communication Protocol",
      passage: `All operational radio communications must follow the standard protocol to ensure clarity and safety.

Before transmitting:
1. Check the channel is clear by listening for 3 seconds
2. Press and hold the transmit button for 1 second before speaking
3. State your call sign and the recipient's call sign

Message structure:
- State "This is [your call sign] calling [recipient call sign]"
- Wait for acknowledgement before continuing
- Deliver your message clearly and concisely
- End with "Over" if expecting a reply, or "Out" if the conversation is complete

Priority messages use the prefix "URGENT" and take precedence over all other communications. Emergency messages use "EMERGENCY" and require all other users to maintain radio silence.

All radio communications are recorded. Staff must not use inappropriate language or discuss non-operational matters on company radio channels.`,
      questions: [
        {
          question: "How long should you listen before transmitting?",
          options: [
            { text: "1 second", correct: false },
            { text: "3 seconds", correct: true },
            { text: "5 seconds", correct: false },
            { text: "10 seconds", correct: false },
          ],
        },
        {
          question: "What word ends a conversation with no reply expected?",
          options: [
            { text: "Over", correct: false },
            { text: "Complete", correct: false },
            { text: "Out", correct: true },
            { text: "Finished", correct: false },
          ],
        },
        {
          question: "What prefix is used for the highest priority messages?",
          options: [
            { text: "URGENT", correct: false },
            { text: "PRIORITY", correct: false },
            { text: "EMERGENCY", correct: true },
            { text: "CRITICAL", correct: false },
          ],
        },
        {
          question: "How long should you hold the transmit button before speaking?",
          options: [
            { text: "Immediately", correct: false },
            { text: "1 second", correct: true },
            { text: "3 seconds", correct: false },
            { text: "5 seconds", correct: false },
          ],
        },
      ],
    },
    {
      title: "Shift Handover Requirements",
      passage: `A proper shift handover is essential for operational continuity and safety. The outgoing staff member is responsible for ensuring complete information transfer.

Handover checklist:
1. Review the shift log together
2. Highlight any ongoing incidents or issues
3. Confirm equipment status (any faults or limitations)
4. Identify any passengers requiring continued assistance
5. Note any expected events (VIP arrivals, engineering works, etc.)

The handover must be conducted face-to-face at the workstation. Telephone handovers are only permitted in exceptional circumstances with supervisor approval.

Both parties must sign the handover sheet with the time recorded. The incoming staff member should not accept the handover if they have concerns - these must be escalated to a supervisor before the outgoing staff member leaves.

Minimum handover time is 10 minutes. Rushed handovers have been identified as a contributing factor in operational incidents.`,
      questions: [
        {
          question: "What is the minimum time allowed for a handover?",
          options: [
            { text: "5 minutes", correct: false },
            { text: "10 minutes", correct: true },
            { text: "15 minutes", correct: false },
            { text: "20 minutes", correct: false },
          ],
        },
        {
          question: "When are telephone handovers permitted?",
          options: [
            { text: "Never", correct: false },
            { text: "In exceptional circumstances with supervisor approval", correct: true },
            { text: "When the station is busy", correct: false },
            { text: "For night shifts only", correct: false },
          ],
        },
        {
          question: "What should the incoming staff member do if they have concerns?",
          options: [
            { text: "Accept the handover and raise concerns later", correct: false },
            { text: "Refuse to start their shift", correct: false },
            { text: "Escalate to a supervisor before the outgoing staff leaves", correct: true },
            { text: "Document concerns in the shift log", correct: false },
          ],
        },
        {
          question: "Who is responsible for ensuring complete information transfer?",
          options: [
            { text: "The incoming staff member", correct: false },
            { text: "The outgoing staff member", correct: true },
            { text: "The supervisor", correct: false },
            { text: "Both equally", correct: false },
          ],
        },
      ],
    },
  ],
  2: [
    {
      title: "Faulty Equipment Procedures",
      passage: `When equipment is found to be faulty, staff must follow the correct labelling and reporting procedure to ensure safety and proper documentation.

Equipment labels:
- RED LABEL: Equipment poses immediate harm risk - must not be used under any circumstances until repaired
- AMBER LABEL: Equipment has reduced functionality - can be used with supervisor approval
- GREEN LABEL: Minor fault logged - equipment can be used normally while awaiting repair

Procedure for faulty equipment:
1. Immediately stop using the equipment
2. Attach the appropriate coloured label with date, time, and your staff number
3. Report the fault to the Equipment Maintenance Team (EMT) via radio channel 7
4. Log the fault in the Equipment Defect Book, including a full description
5. Inform your supervisor before end of shift

Equipment with RED labels must be physically isolated where possible (unplugged, locked out) to prevent accidental use. Only qualified maintenance staff may remove RED labels.`,
      questions: [
        {
          question: "What colour label indicates equipment poses an immediate harm risk?",
          options: [
            { text: "Green", correct: false },
            { text: "Amber", correct: false },
            { text: "Red", correct: true },
            { text: "Yellow", correct: false },
          ],
        },
        {
          question: "Which radio channel is used to report faults to EMT?",
          options: [
            { text: "Channel 4", correct: false },
            { text: "Channel 7", correct: true },
            { text: "Channel 9", correct: false },
            { text: "Channel 1", correct: false },
          ],
        },
        {
          question: "Who can remove a RED label from equipment?",
          options: [
            { text: "Any supervisor", correct: false },
            { text: "The person who applied it", correct: false },
            { text: "Qualified maintenance staff only", correct: true },
            { text: "Duty manager", correct: false },
          ],
        },
        {
          question: "When can AMBER labelled equipment be used?",
          options: [
            { text: "Never", correct: false },
            { text: "With supervisor approval", correct: true },
            { text: "In emergencies only", correct: false },
            { text: "After 24 hours", correct: false },
          ],
        },
      ],
    },
    {
      title: "Emergency Evacuation Procedures",
      passage: `In the event of an emergency requiring evacuation, the following procedure must be followed:

Assembly Points:
- Platform staff: Assembly Point A (main car park)
- Train crew: Assembly Point B (staff building east)
- Office staff: Assembly Point C (visitor centre)

Evacuation roles:
The Fire Marshal (identified by red helmet) takes command of the evacuation. Deputy Fire Marshals (orange helmets) are responsible for checking all areas are clear.

Evacuation sequence:
1. On hearing the alarm, stop all activities immediately
2. Do not collect personal belongings
3. Leave via the nearest marked emergency exit
4. Do not use lifts - use stairs only
5. Report to your designated assembly point
6. Remain at the assembly point until roll call is complete
7. Only return to the building when the "all clear" is given by the Fire Marshal

Staff must account for any visitors or contractors who were with them at the time of the alarm.`,
      questions: [
        {
          question: "Where should train crew assemble during an evacuation?",
          options: [
            { text: "Assembly Point A (main car park)", correct: false },
            { text: "Assembly Point B (staff building east)", correct: true },
            { text: "Assembly Point C (visitor centre)", correct: false },
            { text: "The nearest platform", correct: false },
          ],
        },
        {
          question: "What colour helmet identifies the Fire Marshal?",
          options: [
            { text: "Orange", correct: false },
            { text: "Yellow", correct: false },
            { text: "Red", correct: true },
            { text: "White", correct: false },
          ],
        },
        {
          question: "Who gives the 'all clear' to return to the building?",
          options: [
            { text: "Deputy Fire Marshal", correct: false },
            { text: "Station Manager", correct: false },
            { text: "Fire Marshal", correct: true },
            { text: "Emergency Services", correct: false },
          ],
        },
        {
          question: "What must staff do regarding visitors during evacuation?",
          options: [
            { text: "Direct them to the main exit", correct: false },
            { text: "Account for them at the assembly point", correct: true },
            { text: "Leave them to find their own way", correct: false },
            { text: "Take them to the fire marshal", correct: false },
          ],
        },
      ],
    },
    {
      title: "Lost Property Handling",
      passage: `All lost property must be handled according to company procedure to protect both the property and staff from accusations of theft.

Immediate actions:
1. Note the exact location and time the item was found
2. If the item contains identification, attempt to locate the owner via PA announcement
3. If no owner is found within 15 minutes, take the item to the Lost Property Office

Documentation requirements:
- Complete Form LP-22 with full description of item
- Include any distinguishing features (brand, colour, damage)
- Note the approximate value (under £50, £50-200, over £200)
- Items over £200 must be countersigned by a supervisor

High-value items (cash over £50, electronics, jewellery) must be placed in a tamper-evident bag and stored in the secure cabinet. Two staff members must witness the bagging and both must sign the form.

Lost property is retained for 3 months before being disposed of or donated to charity.`,
      questions: [
        {
          question: "How long should staff wait for an owner before taking items to Lost Property?",
          options: [
            { text: "5 minutes", correct: false },
            { text: "15 minutes", correct: true },
            { text: "30 minutes", correct: false },
            { text: "1 hour", correct: false },
          ],
        },
        {
          question: "What value of items requires supervisor countersignature?",
          options: [
            { text: "Over £20", correct: false },
            { text: "Over £50", correct: false },
            { text: "Over £100", correct: false },
            { text: "Over £200", correct: true },
          ],
        },
        {
          question: "How many staff must witness bagging of high-value items?",
          options: [
            { text: "One", correct: false },
            { text: "Two", correct: true },
            { text: "Three", correct: false },
            { text: "A supervisor only", correct: false },
          ],
        },
        {
          question: "How long is lost property retained?",
          options: [
            { text: "1 month", correct: false },
            { text: "2 months", correct: false },
            { text: "3 months", correct: true },
            { text: "6 months", correct: false },
          ],
        },
      ],
    },
    {
      title: "Customer Complaint Handling",
      passage: `All customer complaints must be taken seriously and handled with professionalism. The aim is to resolve issues at the first point of contact where possible.

Complaint categories:
- Category 1: Safety concerns - escalate immediately to supervisor
- Category 2: Service failures - attempt resolution, log in system
- Category 3: Staff conduct - record and escalate to supervisor
- Category 4: General feedback - log and thank customer

Response procedure:
1. Listen actively without interrupting
2. Apologise for the inconvenience (this is not an admission of fault)
3. Ask clarifying questions to understand the full issue
4. Offer a solution within your authority
5. If you cannot resolve, escalate to appropriate person

Written complaints receive a response within 5 working days. Telephone or in-person complaints should be resolved or escalated within 24 hours. All complaints must be logged in the Customer Feedback System (CFS) by end of shift.`,
      questions: [
        {
          question: "Which complaint category requires immediate escalation?",
          options: [
            { text: "Service failures", correct: false },
            { text: "Safety concerns", correct: true },
            { text: "Staff conduct", correct: false },
            { text: "General feedback", correct: false },
          ],
        },
        {
          question: "Within how many days must written complaints receive a response?",
          options: [
            { text: "2 working days", correct: false },
            { text: "5 working days", correct: true },
            { text: "7 working days", correct: false },
            { text: "10 working days", correct: false },
          ],
        },
        {
          question: "What is the deadline for logging complaints in CFS?",
          options: [
            { text: "Immediately", correct: false },
            { text: "Within 1 hour", correct: false },
            { text: "By end of shift", correct: true },
            { text: "Within 24 hours", correct: false },
          ],
        },
        {
          question: "What should be the first step when handling a complaint?",
          options: [
            { text: "Apologise", correct: false },
            { text: "Listen actively without interrupting", correct: true },
            { text: "Offer a solution", correct: false },
            { text: "Ask for their contact details", correct: false },
          ],
        },
      ],
    },
  ],
  3: [
    {
      title: "Speed Restrictions and Signalling",
      passage: `Temporary speed restrictions (TSRs) are implemented when track conditions require reduced speeds for safety. Drivers must comply with all TSRs regardless of timetable pressures.

TSR Categories:
- Category A: 20mph maximum - severe track defect, requires inspection after each passage
- Category B: 40mph maximum - moderate defect, standard monitoring
- Category C: 60mph maximum - minor defect, weekly inspection sufficient

TSR Indicators:
TSRs are indicated by yellow diamond signs showing the speed limit. An "S" board marks the start of the restriction, and a "T" board marks the termination point.

Special Rules:
1. If a TSR sign is damaged or unclear, assume the most restrictive speed (20mph)
2. Multiple TSRs in sequence: maintain the lowest speed until passing the final "T" board
3. If a driver believes a TSR is missing, they must report immediately and proceed at caution (40mph maximum)
4. Night operations: all Category B and C TSRs require additional 10mph reduction

TSRs take precedence over line speed limits but not over signal aspects.`,
      questions: [
        {
          question: "What is the maximum speed for a Category A restriction?",
          options: [
            { text: "40mph", correct: false },
            { text: "60mph", correct: false },
            { text: "20mph", correct: true },
            { text: "30mph", correct: false },
          ],
        },
        {
          question: "What should a driver do if a TSR sign is unclear?",
          options: [
            { text: "Proceed at line speed", correct: false },
            { text: "Assume 20mph", correct: true },
            { text: "Stop and call control", correct: false },
            { text: "Assume 40mph", correct: false },
          ],
        },
        {
          question: "What board marks the end of a TSR?",
          options: [
            { text: "E board", correct: false },
            { text: "S board", correct: false },
            { text: "T board", correct: true },
            { text: "R board", correct: false },
          ],
        },
        {
          question: "What additional rule applies to TSRs during night operations?",
          options: [
            { text: "All TSRs are cancelled", correct: false },
            { text: "Category B and C require additional 10mph reduction", correct: true },
            { text: "Only Category A applies", correct: false },
            { text: "Double the indicated speed limit", correct: false },
          ],
        },
      ],
    },
    {
      title: "Passenger Assistance Procedures",
      passage: `Staff must provide assistance to passengers who require it, following the Turn-Up-and-Go (TUAG) service principles. Assistance should be provided within 10 minutes of a passenger presenting at the station.

Assistance Categories:
- Physical: wheelchair users, mobility impaired, heavy luggage
- Sensory: visually impaired, hearing impaired
- Cognitive: learning difficulties, dementia, anxiety

Ramp Deployment:
Manual ramps must be deployed by two staff members working together. One member positions the ramp while the other ensures the train doors remain open. The ramp must be placed at a gradient no steeper than 1:12.

Communication:
1. Always ask the passenger how they would like to be assisted
2. Speak directly to the passenger, not to any companion
3. Describe any steps or obstacles clearly
4. Confirm the destination station and alert staff there via the control room

Documentation:
All assistance provided must be logged in the Passenger Assistance Record (PAR) including time of request, type of assistance, and staff member name.`,
      questions: [
        {
          question: "What is the target time for providing assistance under TUAG?",
          options: [
            { text: "5 minutes", correct: false },
            { text: "10 minutes", correct: true },
            { text: "15 minutes", correct: false },
            { text: "20 minutes", correct: false },
          ],
        },
        {
          question: "How many staff members are required to deploy a manual ramp?",
          options: [
            { text: "One", correct: false },
            { text: "Two", correct: true },
            { text: "Three", correct: false },
            { text: "Four", correct: false },
          ],
        },
        {
          question: "What is the maximum gradient for a deployed ramp?",
          options: [
            { text: "1:6", correct: false },
            { text: "1:8", correct: false },
            { text: "1:12", correct: true },
            { text: "1:15", correct: false },
          ],
        },
        {
          question: "Who should staff speak to when assisting a passenger with a companion?",
          options: [
            { text: "The companion", correct: false },
            { text: "The passenger directly", correct: true },
            { text: "Both equally", correct: false },
            { text: "Whoever responds first", correct: false },
          ],
        },
      ],
    },
    {
      title: "Hazardous Substances Protocol",
      passage: `All hazardous substances used in railway operations must be handled according to COSHH (Control of Substances Hazardous to Health) regulations.

Classification symbols:
- Skull and crossbones: Toxic - can cause death or serious harm
- Flame: Flammable - keep away from heat sources
- Exclamation mark: Irritant - may cause skin or eye irritation
- Corrosion symbol: Corrosive - can burn skin and damage materials

Storage requirements:
1. All hazardous substances must be stored in designated locked cabinets
2. Flammable substances: maximum 50 litres per cabinet, stored below 25°C
3. Corrosives must be stored separately from flammables
4. Material Safety Data Sheets (MSDS) must be available within 10 metres of storage

Spill procedure:
For minor spills (under 1 litre), use the appropriate spill kit and dispose of materials in yellow hazardous waste bags. Major spills require area evacuation and specialist response team notification via extension 5555.`,
      questions: [
        {
          question: "What symbol indicates a substance can cause death?",
          options: [
            { text: "Flame", correct: false },
            { text: "Exclamation mark", correct: false },
            { text: "Skull and crossbones", correct: true },
            { text: "Corrosion symbol", correct: false },
          ],
        },
        {
          question: "What is the maximum storage temperature for flammable substances?",
          options: [
            { text: "20°C", correct: false },
            { text: "25°C", correct: true },
            { text: "30°C", correct: false },
            { text: "35°C", correct: false },
          ],
        },
        {
          question: "What extension is used to report major spills?",
          options: [
            { text: "999", correct: false },
            { text: "5555", correct: true },
            { text: "1234", correct: false },
            { text: "4444", correct: false },
          ],
        },
        {
          question: "How close must MSDS be to substance storage?",
          options: [
            { text: "5 metres", correct: false },
            { text: "10 metres", correct: true },
            { text: "15 metres", correct: false },
            { text: "20 metres", correct: false },
          ],
        },
      ],
    },
    {
      title: "Fatigue Management Policy",
      passage: `Railway staff must adhere to fatigue management rules to ensure safe operations. Fatigue is a significant factor in workplace incidents.

Working time limits:
- Maximum shift length: 12 hours (including breaks)
- Minimum rest between shifts: 12 hours
- Maximum consecutive working days: 6 days
- Minimum rest after 6 consecutive days: 48 hours

Night work provisions:
Night shifts (any shift including hours between 11pm and 6am) have additional restrictions:
- Maximum of 4 consecutive night shifts before 48-hour rest
- No driving duties in the 8th hour or beyond of a night shift
- Fatigue declaration required before each night shift

Self-reporting:
Staff must report to their supervisor if they feel too fatigued to work safely. This is a protected report - staff cannot be disciplined for reporting fatigue. However, failing to report fatigue that leads to an incident may result in disciplinary action.`,
      questions: [
        {
          question: "What is the maximum shift length including breaks?",
          options: [
            { text: "8 hours", correct: false },
            { text: "10 hours", correct: false },
            { text: "12 hours", correct: true },
            { text: "14 hours", correct: false },
          ],
        },
        {
          question: "What is the minimum rest required between shifts?",
          options: [
            { text: "8 hours", correct: false },
            { text: "10 hours", correct: false },
            { text: "12 hours", correct: true },
            { text: "14 hours", correct: false },
          ],
        },
        {
          question: "How many consecutive night shifts are allowed?",
          options: [
            { text: "3", correct: false },
            { text: "4", correct: true },
            { text: "5", correct: false },
            { text: "6", correct: false },
          ],
        },
        {
          question: "When are driving duties prohibited during a night shift?",
          options: [
            { text: "After the 6th hour", correct: false },
            { text: "In the 8th hour or beyond", correct: true },
            { text: "After midnight", correct: false },
            { text: "In the last 2 hours", correct: false },
          ],
        },
      ],
    },
  ],
  4: [
    {
      title: "Points and Crossing Operation",
      passage: `Points (also called switches) allow trains to change from one track to another. Incorrect operation can result in derailment or collision.

Types of Points Operation:
- Automatic: Controlled by signalling system, no driver action required
- Semi-automatic: Set by signaller, require driver confirmation via AWS
- Hand-operated: Used in depots, require physical lever operation with handsignaller present

Points Position Indicators:
- Two white lights horizontal = points set for straight route
- Two white lights at 45 degrees = points set for diverging route
- One white light flashing = points in motion, do not pass
- No lights = points failure, stop and contact signaller

Speed Restrictions at Points:
All diverging moves through points are subject to a maximum speed of 25mph unless a higher limit is specifically indicated. Trailing moves (joining routes) can be taken at line speed.

Points Failure Procedure:
1. Stop at the signal protecting the points
2. Contact signaller immediately
3. Do not attempt to pass until authorised
4. If authorised to pass at danger, maximum 5mph over the points
5. Report any unusual movement or noise when passing`,
      questions: [
        {
          question: "What do two horizontal white lights indicate?",
          options: [
            { text: "Points set for diverging route", correct: false },
            { text: "Points set for straight route", correct: true },
            { text: "Points failure", correct: false },
            { text: "Points in motion", correct: false },
          ],
        },
        {
          question: "What is the maximum speed for diverging moves through points?",
          options: [
            { text: "15mph", correct: false },
            { text: "25mph", correct: true },
            { text: "40mph", correct: false },
            { text: "Line speed", correct: false },
          ],
        },
        {
          question: "What should a driver do if point lights show one flashing white?",
          options: [
            { text: "Proceed with caution", correct: false },
            { text: "Do not pass - points in motion", correct: true },
            { text: "Stop and contact signaller", correct: false },
            { text: "Proceed at 5mph", correct: false },
          ],
        },
        {
          question: "If authorised to pass points at danger, what is the maximum speed?",
          options: [
            { text: "5mph", correct: true },
            { text: "10mph", correct: false },
            { text: "15mph", correct: false },
            { text: "Walking pace", correct: false },
          ],
        },
      ],
    },
    {
      title: "Single Line Working Procedures",
      passage: `Single line working (SLW) is implemented when one track of a double line section is blocked, requiring trains in both directions to use the same track.

Authority to Enter Single Line:
Trains must receive a Token or Staff from the signaller before entering the single line section. This physical authority must be carried by the driver throughout the section.

Types of Authority:
- Electric Token: Automatic token instrument at each end
- Train Staff: Physical staff kept at supervising signal box
- Pilotman: A designated person who accompanies each train

Pilotman Procedure:
1. Pilotman must have written authority from both signallers
2. Pilotman must ride on every train through the section
3. Only the pilotman can authorise train movements
4. Pilotman must not leave the section until SLW is cancelled

Speed Limits:
- Entry to single line section: 15mph for first train, then as per token/staff authority
- Exit from single line section: Resume normal line speed only after passing the exit board
- If no specific speed given: maximum 40mph throughout the section

The signaller who sets up SLW is responsible for briefing all drivers before they enter the section.`,
      questions: [
        {
          question: "What must a driver receive before entering a single line section?",
          options: [
            { text: "Radio permission", correct: false },
            { text: "Token or Staff", correct: true },
            { text: "Written authority", correct: false },
            { text: "Signal clearance", correct: false },
          ],
        },
        {
          question: "What is the speed limit for the first train entering the single line?",
          options: [
            { text: "5mph", correct: false },
            { text: "15mph", correct: true },
            { text: "25mph", correct: false },
            { text: "40mph", correct: false },
          ],
        },
        {
          question: "What is the Pilotman's main restriction?",
          options: [
            { text: "Cannot work more than 4 hours", correct: false },
            { text: "Must not leave the section until SLW cancelled", correct: true },
            { text: "Must stay in the signal box", correct: false },
            { text: "Cannot travel on passenger trains", correct: false },
          ],
        },
        {
          question: "If no specific speed is given for SLW, what is the maximum?",
          options: [
            { text: "20mph", correct: false },
            { text: "30mph", correct: false },
            { text: "40mph", correct: true },
            { text: "Line speed", correct: false },
          ],
        },
      ],
    },
    {
      title: "Detrainment Procedures",
      passage: `Detrainment is the controlled evacuation of passengers from a train when it cannot continue to a station platform. This is a last resort measure.

Authorisation:
Detrainment must be authorised by the Train Operations Controller (TOC) except in cases of fire or immediate danger. The driver must confirm:
1. The exact location of the train
2. Number of passengers (estimated)
3. Whether there are passengers with mobility needs
4. Track and weather conditions

Safe Detrainment Requirements:
- Trains on adjacent lines must be stopped
- Third rail power (if applicable) must be confirmed isolated
- Detrainment side must be confirmed safe (away from live tracks)
- Ground-level staff must be in position before doors open

Passenger Instructions:
1. Make announcement explaining the situation
2. Confirm passengers should not leave until instructed
3. Direct passengers to designated exit points
4. Assist mobility-impaired passengers - they exit last with staff assistance
5. Conduct headcount at assembly point`,
      questions: [
        {
          question: "Who must authorise detrainment in normal circumstances?",
          options: [
            { text: "The driver", correct: false },
            { text: "Station supervisor", correct: false },
            { text: "Train Operations Controller (TOC)", correct: true },
            { text: "Senior conductor", correct: false },
          ],
        },
        {
          question: "When can detrainment occur without TOC authorisation?",
          options: [
            { text: "When the delay exceeds 1 hour", correct: false },
            { text: "In cases of fire or immediate danger", correct: true },
            { text: "When passengers request it", correct: false },
            { text: "During night operations", correct: false },
          ],
        },
        {
          question: "When should mobility-impaired passengers exit?",
          options: [
            { text: "First, before other passengers", correct: false },
            { text: "Last, with staff assistance", correct: true },
            { text: "With the general passengers", correct: false },
            { text: "Only if they request to leave", correct: false },
          ],
        },
        {
          question: "What must be stopped before detrainment?",
          options: [
            { text: "All trains on the network", correct: false },
            { text: "Trains on adjacent lines", correct: true },
            { text: "Only the train being evacuated", correct: false },
            { text: "Trains in the same direction", correct: false },
          ],
        },
      ],
    },
    {
      title: "Possession of Line Procedures",
      passage: `A possession is a period when a section of track is closed to normal traffic for engineering work. Different possession types have different requirements.

Possession Categories:
- Absolute: No trains may enter under any circumstances
- Engineering: Work trains may enter with permission
- Inspection: Line open to trains but work party present

Setup Procedure:
1. Possession must be confirmed in the Weekly Operating Notice (WON)
2. Engineering Supervisor (ES) must sign on with signaller
3. Protection equipment placed: detonators at 1/4 mile, 1/2 mile, and 3/4 mile
4. Red flag/light displayed at possession limit
5. ES confirms all protection in place before work begins

Speed Restrictions:
Trains approaching a possession must reduce to 40mph from 1 mile before the possession limit. If the possession is surrendered early, the first train through is limited to 20mph until the driver confirms the line is clear.

Surrender Procedure:
Only the Engineering Supervisor can surrender the possession. All workers and equipment must be clear, and protection removed in reverse order of placement.`,
      questions: [
        {
          question: "What type of possession allows no trains under any circumstances?",
          options: [
            { text: "Engineering", correct: false },
            { text: "Inspection", correct: false },
            { text: "Absolute", correct: true },
            { text: "Temporary", correct: false },
          ],
        },
        {
          question: "At what distances are detonators placed?",
          options: [
            { text: "1/4, 1/2, and 1 mile", correct: false },
            { text: "1/4, 1/2, and 3/4 mile", correct: true },
            { text: "1/2, 1, and 1.5 miles", correct: false },
            { text: "200m, 400m, and 600m", correct: false },
          ],
        },
        {
          question: "What speed limit applies 1 mile before a possession?",
          options: [
            { text: "20mph", correct: false },
            { text: "40mph", correct: true },
            { text: "60mph", correct: false },
            { text: "Line speed", correct: false },
          ],
        },
        {
          question: "Who can surrender a possession?",
          options: [
            { text: "Any worker", correct: false },
            { text: "The signaller", correct: false },
            { text: "Engineering Supervisor only", correct: true },
            { text: "Train Operations Controller", correct: false },
          ],
        },
      ],
    },
  ],
  5: [
    {
      title: "AWS and TPWS Systems",
      passage: `The Automatic Warning System (AWS) and Train Protection Warning System (TPWS) work together to enhance driver safety and prevent signals passed at danger (SPADs).

AWS Operation:
- AWS magnet located 180m before each signal
- Clear signal: bell sounds, no action required
- Caution/Danger signal: horn sounds, driver must acknowledge within 2.7 seconds
- Failure to acknowledge: automatic brake application

TPWS Operation:
TPWS provides two types of intervention:
1. Overspeed Sensor System (OSS): Triggers brake if approaching too fast for a stop signal
2. Train Stop System (TSS): Triggers brake if signal passed at danger

TPWS equipped signals are identified by a small "TS" plate. Not all signals have TPWS fitted.

Isolation Procedures:
AWS/TPWS may only be isolated with control room permission and only when:
- System has failed (evidenced by continuous horn or brake application)
- Fault is logged and technical support requested
- Speed restricted to 40mph until system restored

Special Instructions:
After any TPWS brake intervention, the driver must:
1. Stop completely and apply parking brake
2. Contact signaller to report intervention
3. Not move until authorised
4. Record intervention in train log`,
      questions: [
        {
          question: "How far before a signal is the AWS magnet located?",
          options: [
            { text: "100m", correct: false },
            { text: "150m", correct: false },
            { text: "180m", correct: true },
            { text: "200m", correct: false },
          ],
        },
        {
          question: "How long does a driver have to acknowledge an AWS warning?",
          options: [
            { text: "2.0 seconds", correct: false },
            { text: "2.7 seconds", correct: true },
            { text: "3.5 seconds", correct: false },
            { text: "5.0 seconds", correct: false },
          ],
        },
        {
          question: "What speed restriction applies when AWS/TPWS is isolated?",
          options: [
            { text: "20mph", correct: false },
            { text: "40mph", correct: true },
            { text: "60mph", correct: false },
            { text: "No restriction", correct: false },
          ],
        },
        {
          question: "What identifies signals equipped with TPWS?",
          options: [
            { text: "Yellow marker board", correct: false },
            { text: "A small 'TS' plate", correct: true },
            { text: "Flashing lights", correct: false },
            { text: "Double signal heads", correct: false },
          ],
        },
      ],
    },
    {
      title: "Level Crossing Operations",
      passage: `Level crossings are high-risk locations requiring strict adherence to procedures. Different types of crossings have different operational requirements.

Crossing Types:
- AHB (Automatic Half Barrier): Barriers lower automatically when train approaches, controlled by track circuits
- AOCL (Automatic Open Crossing Locally monitored): No barriers, lights and audible warning only
- MCB (Manually Controlled Barrier): Signaller operates barriers remotely
- UWC (User Worked Crossing): User must check for trains and open gates themselves

Driver Responsibilities:
1. Sound horn at whistle board (located 400m before crossing)
2. Observe crossing clear before passing
3. Report any obstructions immediately
4. Report any barrier or light failures

Emergency Procedures:
If a vehicle is stuck on the crossing:
1. Driver must make emergency brake application
2. Sound horn continuously
3. After stopping, contact signaller immediately
4. Do not attempt to move train until crossing confirmed clear

For UWC crossings, if no telephone is provided, the driver must stop and personally check the crossing is clear before proceeding.`,
      questions: [
        {
          question: "How far before a level crossing is the whistle board located?",
          options: [
            { text: "200m", correct: false },
            { text: "300m", correct: false },
            { text: "400m", correct: true },
            { text: "500m", correct: false },
          ],
        },
        {
          question: "What type of crossing has no barriers, only lights and audible warning?",
          options: [
            { text: "AHB", correct: false },
            { text: "AOCL", correct: true },
            { text: "MCB", correct: false },
            { text: "UWC", correct: false },
          ],
        },
        {
          question: "If a vehicle is stuck on a crossing, what should the driver do first?",
          options: [
            { text: "Sound horn continuously", correct: false },
            { text: "Contact signaller", correct: false },
            { text: "Make emergency brake application", correct: true },
            { text: "Check crossing is clear", correct: false },
          ],
        },
        {
          question: "At a UWC crossing without a telephone, what must the driver do?",
          options: [
            { text: "Proceed at caution", correct: false },
            { text: "Sound horn and continue", correct: false },
            { text: "Stop and personally check crossing is clear", correct: true },
            { text: "Wait for signaller permission", correct: false },
          ],
        },
      ],
    },
    {
      title: "Degraded Mode Operations",
      passage: `Degraded mode refers to operating conditions when normal systems or procedures cannot be followed. This requires heightened awareness and specific protocols.

Common Degraded Mode Scenarios:
- Signalling failure: operate under Rule Book procedures
- Radio failure: use lineside telephones, proceed at caution
- Points failure: manual operation with handsignaller
- AWS/TPWS failure: reduced speed, enhanced vigilance

Communication During Degraded Mode:
When radio fails, drivers must:
1. Stop at the next signal or safe location
2. Use Signal Post Telephone (SPT) or GSM-R emergency call
3. If no telephone available, protect train and walk to signal box
4. Do not proceed without verbal authority from signaller

Speed Restrictions:
- Signalling failure with verbal authority: 20mph maximum
- Radio failure: 40mph maximum until next staffed location
- Multiple failures: stop and await instructions

Recovery:
Normal operations resume only when:
- The failed system is confirmed restored by competent person
- Driver has received clearance from control
- Any temporary speed restrictions are lifted by notice`,
      questions: [
        {
          question: "What is the maximum speed during signalling failure with verbal authority?",
          options: [
            { text: "10mph", correct: false },
            { text: "20mph", correct: true },
            { text: "30mph", correct: false },
            { text: "40mph", correct: false },
          ],
        },
        {
          question: "What should a driver do first when radio fails?",
          options: [
            { text: "Continue at reduced speed", correct: false },
            { text: "Stop at the next signal or safe location", correct: true },
            { text: "Make an emergency call", correct: false },
            { text: "Return to the last station", correct: false },
          ],
        },
        {
          question: "What is the maximum speed during radio failure?",
          options: [
            { text: "20mph", correct: false },
            { text: "30mph", correct: false },
            { text: "40mph", correct: true },
            { text: "Line speed", correct: false },
          ],
        },
        {
          question: "What must happen before normal operations can resume?",
          options: [
            { text: "Shift change", correct: false },
            { text: "24-hour waiting period", correct: false },
            { text: "System confirmed restored by competent person", correct: true },
            { text: "Written notification to driver", correct: false },
          ],
        },
      ],
    },
    {
      title: "Accident and Incident Investigation",
      passage: `All accidents and incidents must be investigated to prevent recurrence. Staff involvement in investigations is mandatory when requested.

Investigation Categories:
- Category 1: Fatal or major injury - ORR investigation required
- Category 2: Significant incident - internal investigation within 72 hours
- Category 3: Near miss or minor incident - local investigation within 7 days

Staff Responsibilities:
1. Do not disturb the scene unless necessary for safety
2. Record witness names and contact details
3. Preserve any physical evidence
4. Write personal statement within 24 hours while memory is fresh
5. Cooperate fully with investigators

Evidence Preservation:
CCTV footage must be secured within 6 hours - contact Control to arrange. Train data recorders must not be accessed except by authorised personnel. Photographs should be taken of the scene, any damage, and relevant signage.

Post-Investigation:
Staff may be required to attend formal interviews. They have the right to:
- 24 hours notice of interview time
- Trade union representative present
- Review their own statement before interview
- Refuse to answer questions if facing potential prosecution`,
      questions: [
        {
          question: "Within what timeframe must CCTV footage be secured?",
          options: [
            { text: "2 hours", correct: false },
            { text: "6 hours", correct: true },
            { text: "12 hours", correct: false },
            { text: "24 hours", correct: false },
          ],
        },
        {
          question: "When must a personal statement be written?",
          options: [
            { text: "Immediately", correct: false },
            { text: "Within 24 hours", correct: true },
            { text: "Within 72 hours", correct: false },
            { text: "Within 7 days", correct: false },
          ],
        },
        {
          question: "What type of incident requires ORR investigation?",
          options: [
            { text: "Category 3", correct: false },
            { text: "Category 2", correct: false },
            { text: "Category 1 (fatal or major injury)", correct: true },
            { text: "All categories", correct: false },
          ],
        },
        {
          question: "What notice must staff receive before a formal interview?",
          options: [
            { text: "12 hours", correct: false },
            { text: "24 hours", correct: true },
            { text: "48 hours", correct: false },
            { text: "72 hours", correct: false },
          ],
        },
      ],
    },
  ],
};

// Device Mode Selector Component
const DeviceModeSelector = ({ onSelect }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Larger layout</p>
          </div>
        </Button>

        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Tablet/Phone</p>
            <p className="text-xs text-[#64748B]">Touch-friendly</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Instructions Screen
const InstructionsScreen = ({ level, onStart }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-6">
      <div className="text-center mb-6">
        <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51]">
          Level {level}: {LEVEL_LABELS[level] || 'Advanced'}
        </Badge>
        <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
          Rules Acquisition Test
        </h2>
        <p className="text-[#64748B]">
          Develops skills to improve performance on RAAT assessments
        </p>
      </div>

      <div className="space-y-4 mb-6">
        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">1</div>
          <p className="text-sm text-[#64748B]">You will read a <strong>passage about rules or procedures</strong>.</p>
        </div>
        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">2</div>
          <p className="text-sm text-[#64748B]">Answer multiple-choice questions about the passage.</p>
        </div>
        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold">
            <CheckCircle className="w-4 h-4" />
          </div>
          <p className="text-sm text-green-700"><strong>You CAN refer back to the passage</strong> while answering - this is an open-book test!</p>
        </div>
        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-sm font-bold">4</div>
          <p className="text-sm text-[#64748B]">All answers must be based <strong>only</strong> on information in the passage.</p>
        </div>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-6">
        <div className="flex items-center gap-2 text-blue-700">
          <Info className="w-4 h-4" />
          <span className="text-sm font-medium">RIS-3751-TOM</span>
        </div>
        <p className="text-sm text-blue-600 mt-1">
          This exercise develops skills assessed by RAAT - measuring ability to read, comprehend, and correctly apply written rules and procedures.
        </p>
      </div>

      <Button
        onClick={onStart}
        className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white"
        data-testid="begin-test-btn"
      >
        <Play className="w-4 h-4 mr-2" />
        Begin Test (2 passages)
      </Button>
    </CardContent>
  </Card>
);

// Passage Display with collapsible
const PassageDisplay = ({ title, passage, isExpanded, onToggle }) => (
  <Card className="border-slate-200 mb-4">
    <CardContent className="pt-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
        data-testid="toggle-passage-btn"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#0B1D51]" />
          <h3 className="font-heading font-bold text-[#0B1D51]">{title}</h3>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-[#64748B]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#64748B]" />
        )}
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-[#374151] whitespace-pre-line leading-relaxed">
                {passage}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {!isExpanded && (
        <p className="text-xs text-[#64748B] mt-2">Click to expand passage</p>
      )}
    </CardContent>
  </Card>
);

// Question Display
const QuestionDisplay = ({
  questionNumber,
  totalQuestions,
  question,
  options,
  selectedAnswer,
  onSelect,
  onSubmit,
  showFeedback,
  isCorrect,
}) => (
  <Card className="border-slate-200">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between mb-4">
        <Badge variant="outline">Question {questionNumber} of {totalQuestions}</Badge>
        {showFeedback && (
          <Badge className={isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
            {isCorrect ? 'Correct!' : 'Incorrect'}
          </Badge>
        )}
      </div>

      <p className="text-[#0B1D51] font-medium mb-4">{question}</p>

      <div className="space-y-2">
        {options.map((option, index) => {
          const letter = String.fromCharCode(65 + index);
          const isSelected = selectedAnswer === index;
          const showCorrect = showFeedback && option.correct;
          const showWrong = showFeedback && isSelected && !option.correct;

          return (
            <button
              key={index}
              onClick={() => !showFeedback && onSelect(index)}
              disabled={showFeedback}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                showCorrect
                  ? 'border-green-500 bg-green-50'
                  : showWrong
                  ? 'border-red-500 bg-red-50'
                  : isSelected
                  ? 'border-[#0B1D51] bg-[#0B1D51]/5'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`option-${letter}`}
            >
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  showCorrect
                    ? 'bg-green-500 text-white'
                    : showWrong
                    ? 'bg-red-500 text-white'
                    : isSelected
                    ? 'bg-[#0B1D51] text-white'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {letter}
                </span>
                <span className="text-sm text-[#374151]">{option.text}</span>
                {showCorrect && <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />}
                {showWrong && <XCircle className="w-5 h-5 text-red-500 ml-auto" />}
              </div>
            </button>
          );
        })}
      </div>

      {!showFeedback && (
        <Button
          onClick={onSubmit}
          disabled={selectedAnswer === null}
          className="w-full mt-4 bg-[#22C55E] hover:bg-[#16A34A] text-white"
          data-testid="submit-answer-btn"
        >
          Submit Answer
        </Button>
      )}
    </CardContent>
  </Card>
);

// Main Game Component
const RulesAcquisitionGame = ({ level = 1, onComplete, onExit }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [passageExpanded, setPassageExpanded] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionResults, setQuestionResults] = useState([]);

  const passages = useMemo(() => {
    const levelPassages = RAAT_PASSAGES[level] || RAAT_PASSAGES[1];
    // Shuffle passages and shuffle options within each question
    return shuffleArray(levelPassages).slice(0, 2).map(passage => ({
      ...passage,
      questions: passage.questions.map(q => ({
        ...q,
        shuffledOptions: shuffleArray([...q.options])
      }))
    }));
  }, [level]);

  const currentPassage = passages[currentPassageIndex];
  const currentQuestion = currentPassage?.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex >= currentPassage?.questions.length - 1;
  const isLastPassage = currentPassageIndex >= passages.length - 1;

  const handleDeviceSelect = (mode) => {
    setDeviceMode(mode);
    setGameState(GAME_STATES.INSTRUCTIONS);
  };

  const handleStartTest = () => {
    setTotalQuestions(passages.reduce((acc, p) => acc + p.questions.length, 0));
    setGameState(GAME_STATES.PLAYING);
  };

  const handleSelectAnswer = (index) => {
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = () => {
    const isCorrect = currentQuestion.shuffledOptions[selectedAnswer].correct;
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    setQuestionResults(prev => [...prev, { correct: isCorrect, passageIndex: currentPassageIndex }]);
    setShowFeedback(true);
  };

  const handleNextQuestion = () => {
    setSelectedAnswer(null);
    setShowFeedback(false);

    if (isLastQuestion) {
      if (isLastPassage) {
        setGameState(GAME_STATES.FINISHED);
      } else {
        setCurrentPassageIndex(prev => prev + 1);
        setCurrentQuestionIndex(0);
        setPassageExpanded(true);
      }
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleRestart = () => {
    setCurrentPassageIndex(0);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setScore(0);
    setQuestionResults([]);
    setPassageExpanded(true);
    setGameState(GAME_STATES.INSTRUCTIONS);
  };

  const handleFinish = () => {
    const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
    onComplete?.({
      score,
      maxScore: totalQuestions,
      accuracy: percentage,
      timeSpent: 0, // RAAT doesn't track time
      level,
      questionResults,
    });
  };

  // Render based on game state
  if (gameState === GAME_STATES.READY) {
    return (
      <div className="max-w-md mx-auto">
        <DeviceModeSelector onSelect={handleDeviceSelect} />
      </div>
    );
  }

  if (gameState === GAME_STATES.INSTRUCTIONS) {
    return (
      <div className="max-w-lg mx-auto">
        <InstructionsScreen level={level} onStart={handleStartTest} />
      </div>
    );
  }

  if (gameState === GAME_STATES.PLAYING) {
    const overallQuestionNumber = passages
      .slice(0, currentPassageIndex)
      .reduce((acc, p) => acc + p.questions.length, 0) + currentQuestionIndex + 1;

    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <Badge variant="outline">Passage {currentPassageIndex + 1} of {passages.length}</Badge>
            <span className="text-sm text-[#64748B]">Score: {score}/{overallQuestionNumber - 1}</span>
          </div>
          <Progress value={(overallQuestionNumber / totalQuestions) * 100} className="h-2" />
        </div>

        <PassageDisplay
          title={currentPassage.title}
          passage={currentPassage.passage}
          isExpanded={passageExpanded}
          onToggle={() => setPassageExpanded(prev => !prev)}
        />

        <QuestionDisplay
          questionNumber={overallQuestionNumber}
          totalQuestions={totalQuestions}
          question={currentQuestion.question}
          options={currentQuestion.shuffledOptions}
          selectedAnswer={selectedAnswer}
          onSelect={handleSelectAnswer}
          onSubmit={handleSubmitAnswer}
          showFeedback={showFeedback}
          isCorrect={showFeedback && currentQuestion.shuffledOptions[selectedAnswer]?.correct}
        />

        {showFeedback && (
          <Button
            onClick={handleNextQuestion}
            className="w-full mt-4 bg-[#0B1D51] hover:bg-[#0B1D51]/90 text-white"
            data-testid="next-question-btn"
          >
            {isLastQuestion && isLastPassage ? 'View Results' : isLastQuestion ? 'Next Passage' : 'Next Question'}
          </Button>
        )}
      </div>
    );
  }

  if (gameState === GAME_STATES.FINISHED) {
    const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
    return (
      <div className="max-w-lg mx-auto">
        <Card className="border-slate-200">
          <CardContent className="pt-8 text-center">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${percentage >= 75 ? 'bg-green-100' : percentage >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
              <FileText className={`w-10 h-10 ${percentage >= 75 ? 'text-green-600' : percentage >= 50 ? 'text-amber-600' : 'text-red-600'}`} />
            </div>

            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Test Complete!
            </h2>

            <div className="text-4xl font-bold text-[#0B1D51] mb-2">
              {percentage}%
            </div>

            <p className="text-[#64748B] mb-6">
              You answered {score} out of {totalQuestions} questions correctly
            </p>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {passages.map((passage, pIndex) => {
                const passageResults = questionResults.filter(r => r.passageIndex === pIndex);
                const passageScore = passageResults.filter(r => r.correct).length;
                const passageTotal = passage.questions.length;
                return (
                  <div
                    key={pIndex}
                    className={`p-3 rounded-lg ${passageScore >= passageTotal * 0.75 ? 'bg-green-100' : passageScore >= passageTotal * 0.5 ? 'bg-amber-100' : 'bg-red-100'}`}
                  >
                    <div className="text-xs font-medium text-slate-600 mb-1">Passage {pIndex + 1}</div>
                    <div className="text-lg font-bold">{passageScore}/{passageTotal}</div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleRestart}
                data-testid="restart-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button
                className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white"
                onClick={handleFinish}
                data-testid="finish-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Save Score
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default RulesAcquisitionGame;
